import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import PageLayout from "../components/site/PageLayout";
import { SectionHeading } from "../components/site/SectionHeading";
import { ImgSlot } from "../components/site/ImgSlot";
import { Button } from "../components/ui/button";
import { useContent } from "../lib/content";

const ALBUMS = [
  { id: "workshops", label: "Workshops" },
  { id: "community", label: "Community games" },
  { id: "volunteers", label: "Volunteers" },
  { id: "seniors", label: "Seniors" },
];

// Fallback slots when no gallery items have been uploaded in the CMS yet.
const FALLBACK_SLOTS = [
  { album: "workshops", ratio: "4/3", label: "Workshop 01" },
  { album: "community", ratio: "1/1", label: "Bingo night" },
  { album: "seniors", ratio: "3/4", label: "Portrait" },
  { album: "volunteers", ratio: "4/3", label: "Team" },
  { album: "workshops", ratio: "1/1", label: "Hands-on help" },
  { album: "community", ratio: "4/3", label: "Jeopardy" },
  { album: "seniors", ratio: "1/1", label: "Smiles" },
  { album: "workshops", ratio: "3/4", label: "One-on-one" },
  { album: "volunteers", ratio: "4/3", label: "Group photo" },
];

const Gallery = () => {
  const { content } = useContent();
  const items = Array.isArray(content.gallery) && content.gallery.length ? content.gallery : FALLBACK_SLOTS;
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const list = useMemo(() => filter === "all" ? items : items.filter((s) => s.album === filter), [filter, items]);

  return (
    <PageLayout title="Gallery">
      <section className="relative pt-16 pb-10 overflow-hidden">
        <div className="ambient" aria-hidden />
        <div className="ambient-grid" aria-hidden />
        <div className="container-tt">
          <SectionHeading eyebrow="Gallery" title="Moments from the field." description="Photos are added by our team from each event. Filter below or open any tile for a closer look." />
          <div className="mt-8 flex flex-wrap gap-2" data-testid="gallery-filters">
            {[{id:"all", label:"All"}, ...ALBUMS].map(a => (
              <button
                key={a.id}
                onClick={() => setFilter(a.id)}
                data-testid={`gallery-filter-${a.id}`}
                className={`btn-pill btn-motion h-11 px-5 text-sm border ${filter === a.id ? "bg-stone text-white border-stone shadow-dark" : "bg-white text-stone border-border hover:border-sky/60 hover:text-skyDark"}`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-tt">
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]" data-testid="gallery-grid">
            {list.map((s, i) => (
              <button
                key={`${s.label}-${i}`}
                onClick={() => setOpen(i)}
                className="mb-5 block w-full break-inside-avoid focus:outline-none"
                aria-label={`Open ${s.label}`}
              >
                <ImgSlot src={s.image} label={s.label} ratio={s.ratio} className="card-lift" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {open !== null && list[open] && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] grid place-items-center bg-stone/85 backdrop-blur-sm p-6"
            onClick={() => setOpen(null)}
            data-testid="gallery-lightbox"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="relative w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-end mb-3">
                <Button onClick={() => setOpen(null)} data-testid="lightbox-close" className="btn-pill bg-white text-stone hover:bg-white/90 h-11 px-4"><X size={16} className="mr-1" /> Close</Button>
              </div>
              <ImgSlot src={list[open].image} label={list[open].label} ratio={list[open].ratio} />
              <div className="mt-4 text-white text-sm">{list[open].label} — TechTies</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageLayout>
  );
};

export default Gallery;
